import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CheckSquare, Square, CheckCircle2, Calendar, Plus, X, Save, Home,
  AlertCircle, Clock, CalendarDays, Trash2, Hourglass,
} from "lucide-react";
import http from "@/lib/http";
import { t } from "@/i18n";
import { parseDate } from "@/lib/format";

const QUICK_FILTERS = [
  { k: "today",     label: "Vandaag" },
  { k: "this_week", label: "Deze week" },
  { k: "all",       label: "Alle taken" },
];

export default function Tasks() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [showNew, setShowNew] = useState(false);
  const [selected, setSelected] = useState(() => new Set());

  const load = () => http.get("/api/tasks").then(r => { setTasks(r.data); setLoading(false); });
  useEffect(() => { load(); }, []);

  const toggle = async (task) => {
    await http.patch(`/api/tasks/${task.id}`, { completed: !task.completed });
    await load();
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const clearSelection = () => setSelected(new Set());
  const deleteOne = async (task) => {
    if (!window.confirm(`Taak "${task.title}" verwijderen?`)) return;
    await http.delete(`/api/tasks/${task.id}`);
    setSelected(prev => { const n = new Set(prev); n.delete(task.id); return n; });
    await load();
  };
  const deleteSelected = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!window.confirm(`${ids.length} ${ids.length === 1 ? "taak" : "taken"} verwijderen?`)) return;
    await http.post("/api/tasks/bulk-delete", { ids });
    clearSelection();
    await load();
  };
  const selectAllVisible = (visibleIds) => {
    setSelected(prev => {
      const n = new Set(prev);
      const allInPrev = visibleIds.every(id => n.has(id));
      if (allInPrev) visibleIds.forEach(id => n.delete(id));
      else visibleIds.forEach(id => n.add(id));
      return n;
    });
  };

  // Visible tasks = tasks that pass the quick filter
  const visible = useMemo(() => {
    if (filter === "all") return tasks;
    const today = startOfDay(new Date());
    const endOfWeek = new Date(today); endOfWeek.setDate(today.getDate() + 7);
    return tasks.filter(t => {
      if (t.completed) return false;  // completed always shown in own section, never via filter
      if (!t.due_date) return filter === "all";
      const d = parseDate(t.due_date);
      if (!d) return false;
      if (filter === "today") return startOfDay(d).getTime() === today.getTime();
      if (filter === "this_week") return d <= endOfWeek;
      return true;
    });
  }, [tasks, filter]);

  // Bucket the open tasks
  const buckets = useMemo(() => bucketize(visible), [visible]);
  // Completed always taken from full task list (filter doesn't apply)
  const completed = useMemo(() => tasks.filter(t => t.completed), [tasks]);

  const openLead = (id) => id && navigate(`/lead/${id}`);
  const openFollow = (id) => id && navigate(`/follow-through/${id}`);

  // Currently visible task IDs (open + completed if filter=all)
  const allVisibleIds = useMemo(() => {
    const ids = [];
    [buckets.overdue, buckets.today, buckets.planned].forEach(b => b.forEach(t => ids.push(t.id)));
    if (filter === "all") completed.forEach(t => ids.push(t.id));
    return ids;
  }, [buckets, completed, filter]);
  const allChecked = allVisibleIds.length > 0 && allVisibleIds.every(id => selected.has(id));
  const someChecked = selected.size > 0;

  return (
    <div className="p-8 max-w-[920px] mx-auto fade-in" data-testid="tasks-screen">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="text-[13px] text-[var(--teal-700)] font-medium mb-2 uppercase tracking-[0.14em]">
            {t("nav.tasks")}
          </div>
          <h1 className="font-display text-[32px] leading-tight text-[var(--ink-900)]">
            {t("tasks.title")}
          </h1>
          <p className="text-[14px] text-[var(--ink-500)] mt-1">{t("tasks.sub")}</p>
        </div>
        <button type="button" data-testid="btn-new-task" className="btn btn-primary"
                onClick={() => setShowNew(true)}>
          <Plus size={16} /> Taak toevoegen
        </button>
      </div>

      {/* Quick filter tabs + bulk action bar */}
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <div className="flex items-center gap-1 bg-[var(--sand-100)] border border-[var(--border-soft)] rounded-lg p-1 w-fit"
             data-testid="task-filter-tabs">
          {QUICK_FILTERS.map(f => (
            <button key={f.k} type="button"
                    data-testid={`filter-${f.k}`}
                    onClick={() => setFilter(f.k)}
                    className={`px-3 h-8 rounded-md text-[12px] font-medium transition
                      ${filter === f.k ? "bg-white text-[var(--teal-700)] shadow-sm"
                                       : "text-[var(--ink-500)] hover:text-[var(--ink-900)]"}`}>
              {f.label}
            </button>
          ))}
        </div>

        {someChecked && (
          <div className="flex items-center gap-2 ml-auto" data-testid="bulk-action-bar">
            <span className="text-[13px] text-[var(--ink-700)]">
              <strong data-testid="selected-count">{selected.size}</strong>{" "}
              {selected.size === 1 ? "taak" : "taken"} geselecteerd
            </span>
            <button type="button" onClick={() => selectAllVisible(allVisibleIds)}
                    data-testid="btn-select-all"
                    className="btn btn-ghost !h-9 !px-3 !text-[12px]">
              {allChecked ? "Alles deselecteren" : "Alles selecteren"}
            </button>
            <button type="button" onClick={clearSelection}
                    className="btn btn-ghost !h-9 !px-2 !text-[12px]">
              <X size={13} />
            </button>
            <button type="button" onClick={deleteSelected}
                    data-testid="btn-bulk-delete"
                    className="btn !h-9 !px-3 !text-[12px] bg-[var(--danger,#D6646E)] hover:bg-[#B84850] text-white border-transparent">
              <Trash2 size={13} /> Verwijderen
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-[var(--ink-500)] text-sm">{t("common.loading")}</div>
      ) : tasks.length === 0 ? (
        <div className="card text-center py-16" data-testid="tasks-empty">
          <div className="w-14 h-14 mx-auto rounded-xl bg-[var(--teal-50)] text-[var(--teal-700)] grid place-items-center mb-4">
            <CheckCircle2 size={22} />
          </div>
          <h3 className="font-display text-[20px]">{t("tasks.empty_title")}</h3>
          <p className="text-[14px] text-[var(--ink-500)] mt-2 max-w-md mx-auto">{t("tasks.empty_body")}</p>
        </div>
      ) : (
        <div className="space-y-6">
          <Section icon={AlertCircle} iconClass="text-[var(--danger,#D6646E)]"
                   title="Over tijd" tone="danger" tasks={buckets.overdue}
                   onToggle={toggle} onOpenLead={openLead} onOpenFollow={openFollow}
                   selected={selected} onToggleSelect={toggleSelect} onDelete={deleteOne}
                   testid="section-overdue" />
          <Section icon={Clock} iconClass="text-[#D97706]"
                   title="Vandaag / Dringend" tone="warn" tasks={buckets.today}
                   onToggle={toggle} onOpenLead={openLead} onOpenFollow={openFollow}
                   selected={selected} onToggleSelect={toggleSelect} onDelete={deleteOne}
                   testid="section-today" />
          <Section icon={CalendarDays} iconClass="text-[var(--teal-700)]"
                   title="Gepland" tone="planned" tasks={buckets.planned}
                   onToggle={toggle} onOpenLead={openLead} onOpenFollow={openFollow}
                   selected={selected} onToggleSelect={toggleSelect} onDelete={deleteOne}
                   testid="section-planned" />
          {filter === "all" && completed.length > 0 && (
            <Section icon={CheckCircle2} iconClass="text-[var(--ink-400)]"
                     title="Voltooid" tone="done" tasks={completed}
                     onToggle={toggle} onOpenLead={openLead} onOpenFollow={openFollow}
                     selected={selected} onToggleSelect={toggleSelect} onDelete={deleteOne}
                     testid="section-done" />
          )}
          {buckets.overdue.length === 0 && buckets.today.length === 0 && buckets.planned.length === 0
            && (filter !== "all" || completed.length === 0) && (
            <div className="text-[14px] text-[var(--ink-500)] italic" data-testid="filter-empty">
              Geen taken in deze filter.
            </div>
          )}
        </div>
      )}

      {showNew && (
        <NewTaskDialog onClose={() => setShowNew(false)}
                       onCreated={() => { setShowNew(false); load(); }} />
      )}
    </div>
  );
}

function Section({ icon: Icon, iconClass, title, tone, tasks, onToggle, onOpenLead, onOpenFollow,
                   selected, onToggleSelect, onDelete, testid }) {
  if (!tasks || tasks.length === 0) return null;
  return (
    <section data-testid={testid}>
      <h3 className="text-[12px] uppercase tracking-wider mb-2 flex items-center gap-2 text-[var(--ink-700)]">
        <Icon size={14} className={iconClass} />
        <span>{title}</span>
        <span className="text-[var(--ink-400)] tabular">({tasks.length})</span>
      </h3>
      <ul className="space-y-1.5">
        {tasks.map(task => (
          <TaskRow key={task.id} task={task} tone={tone}
                   isSelected={selected?.has(task.id)}
                   onSelect={() => onToggleSelect(task.id)}
                   onToggle={() => onToggle(task)}
                   onOpenLead={() => onOpenLead(task.lead_id)}
                   onOpenFollow={() => onOpenFollow(task.follow_through_id)}
                   onDelete={() => onDelete(task)} />
        ))}
      </ul>
    </section>
  );
}

function TaskRow({ task, tone, isSelected, onSelect, onToggle, onOpenLead, onOpenFollow, onDelete }) {
  const dueColor = dueDateColor(task.due_date, task.completed);
  const leadLabel = task.lead
    ? `${task.lead.address || ""}${task.lead.postal_code ? " · " + task.lead.postal_code : ""}${task.lead.city ? " " + task.lead.city : ""}`
    : null;
  const followLabel = task.follow_through
    ? `${task.follow_through.address || ""}${task.follow_through.postal_code ? " · " + task.follow_through.postal_code : ""}${task.follow_through.city ? " " + task.follow_through.city : ""}`
    : null;
  const isDone = !!task.completed;
  const creatorName = task.created_by?.name;
  return (
    <li
      data-testid={`task-${task.id}`}
      className={`card !py-2.5 !px-4 flex items-start gap-3 transition-colors group
        ${isDone ? "opacity-60" : ""}
        ${isSelected ? "!bg-[var(--teal-50)] !border-[var(--teal-500)]" : ""}
        ${tone === "danger" && !isDone && !isSelected ? "border-l-[3px] border-l-[var(--danger,#D6646E)]" : ""}
        ${tone === "warn" && !isDone && !isSelected ? "border-l-[3px] border-l-[#D97706]" : ""}`}
    >
      <input
        type="checkbox"
        checked={isSelected || false}
        onChange={onSelect}
        data-testid={`task-select-${task.id}`}
        className="mt-1 accent-[var(--teal-600)] cursor-pointer"
        aria-label="Selecteer taak"
      />
      <button type="button" onClick={onToggle}
              data-testid={`task-toggle-${task.id}`}
              className={`mt-0.5 transition-colors ${isDone ? "text-[var(--teal-600)]" : "text-[var(--ink-400)] hover:text-[var(--teal-600)]"}`}>
        {isDone ? <CheckSquare size={18} /> : <Square size={18} />}
      </button>
      <div className="flex-1 min-w-0">
        <div className={`text-[14px] font-medium truncate ${isDone ? "line-through text-[var(--ink-500)]" : "text-[var(--ink-900)]"}`}>
          {task.title}
        </div>
        <div className="text-[12px] text-[var(--ink-500)] mt-0.5 flex items-center gap-2 flex-wrap">
          {leadLabel && (
            <button type="button" onClick={onOpenLead}
                    data-testid={`task-dossier-${task.id}`}
                    className="hover:text-[var(--teal-700)] flex items-center gap-1 truncate">
              <Home size={11} /> {leadLabel}
            </button>
          )}
          {followLabel && (
            <button type="button" onClick={onOpenFollow}
                    data-testid={`task-follow-${task.id}`}
                    className="hover:text-[var(--teal-700)] flex items-center gap-1 truncate">
              <Hourglass size={11} /> {followLabel}
            </button>
          )}
          {creatorName && (
            <>
              {(leadLabel || followLabel) && <span className="text-[var(--ink-300)]">·</span>}
              <span data-testid={`task-creator-${task.id}`}
                    className="inline-flex items-center gap-1 chip !py-[1px] !px-1.5 !text-[10px]
                               bg-[var(--sand-100)] text-[var(--ink-700)] border-[var(--border-soft)]">
                <Avatar name={creatorName} /> {creatorName}
              </span>
            </>
          )}
        </div>
      </div>
      {task.due_date && (
        <div className={`text-[12px] font-medium tabular shrink-0 flex items-center gap-1 ${dueColor}`}
             data-testid={`task-due-${task.id}`}>
          <Calendar size={11} />
          {formatDue(task.due_date)}
        </div>
      )}
      <button type="button" onClick={onDelete}
              data-testid={`task-delete-${task.id}`}
              title="Verwijderen"
              className="opacity-0 group-hover:opacity-100 p-1.5 rounded text-[var(--ink-400)] hover:bg-[var(--sand-100)] hover:text-[var(--danger)] transition-opacity">
        <Trash2 size={14} />
      </button>
    </li>
  );
}

function Avatar({ name }) {
  const initials = (name || "?")
    .split(/\s+/).map(p => p[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
  return (
    <span className="w-4 h-4 rounded-full bg-[var(--teal-600)] text-white grid place-items-center
                     text-[8px] font-semibold tracking-wider">
      {initials}
    </span>
  );
}

function NewTaskDialog({ onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [leadQuery, setLeadQuery] = useState("");
  const [leadResults, setLeadResults] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!leadQuery.trim()) { setLeadResults([]); return; }
    const id = setTimeout(async () => {
      try {
        const r = await http.get("/api/leads", { params: { all_postcodes: true } });
        const q = leadQuery.toLowerCase();
        setLeadResults(r.data.filter(l =>
          (l.address || "").toLowerCase().includes(q) ||
          (l.postal_code || "").includes(q) ||
          (l.city || "").toLowerCase().includes(q)
        ).slice(0, 6));
      } catch (e) { console.debug("task-dialog lead search filter error", e); }
    }, 200);
    return () => clearTimeout(id);
  }, [leadQuery]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await http.post("/api/tasks", {
        title: title.trim(),
        due_date: dueDate || null,
        lead_id: selectedLead?.id || null,
      });
      onCreated();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Aanmaken mislukt");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/30 z-40 grid place-items-center p-4" data-testid="new-task-dialog">
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4">
        <h2 className="font-display text-[22px]">Nieuwe taak</h2>
        <label className="block">
          <div className="text-[12px] font-medium text-[var(--ink-700)] mb-1">Titel</div>
          <input autoFocus required value={title}
                 onChange={(e) => setTitle(e.target.value)}
                 data-testid="new-task-title"
                 placeholder="Bv. Bel Marie terug"
                 className="w-full h-10 px-3 rounded-lg border border-[var(--border-soft)] text-sm" />
        </label>
        <label className="block">
          <div className="text-[12px] font-medium text-[var(--ink-700)] mb-1">Deadline</div>
          <input type="date" value={dueDate}
                 onChange={(e) => setDueDate(e.target.value)}
                 data-testid="new-task-due"
                 className="w-full h-10 px-3 rounded-lg border border-[var(--border-soft)] text-sm" />
        </label>
        <div className="block">
          <div className="text-[12px] font-medium text-[var(--ink-700)] mb-1">Gekoppeld dossier (optioneel)</div>
          {selectedLead ? (
            <div className="flex items-center gap-2 p-2 rounded-lg border border-[var(--border-soft)]">
              <Home size={14} className="text-[var(--teal-700)]" />
              <div className="flex-1 text-[13px] truncate">
                {selectedLead.address} · {selectedLead.postal_code} {selectedLead.city}
              </div>
              <button type="button" onClick={() => { setSelectedLead(null); setLeadQuery(""); }}
                      className="text-[var(--ink-400)] hover:text-[var(--danger)]">
                <X size={14} />
              </button>
            </div>
          ) : (
            <>
              <input value={leadQuery}
                     onChange={(e) => setLeadQuery(e.target.value)}
                     data-testid="new-task-lead-search"
                     placeholder="Zoek op adres, postcode, …"
                     className="w-full h-10 px-3 rounded-lg border border-[var(--border-soft)] text-sm" />
              {leadResults.length > 0 && (
                <ul className="mt-1 border border-[var(--border-soft)] rounded-lg bg-white shadow-sm max-h-48 overflow-auto">
                  {leadResults.map(l => (
                    <li key={l.id}>
                      <button type="button"
                              data-testid={`new-task-lead-pick-${l.id}`}
                              onClick={() => { setSelectedLead(l); setLeadQuery(""); setLeadResults([]); }}
                              className="w-full text-left px-3 py-2 text-[13px] hover:bg-[var(--sand-100)]">
                        {l.address} · {l.postal_code} {l.city}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
        {err && <div className="text-[12px] text-[var(--danger)]">{err}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn btn-ghost">Annuleren</button>
          <button type="submit" disabled={busy} data-testid="btn-save-task" className="btn btn-primary">
            <Save size={14} /> {busy ? "…" : "Aanmaken"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ---------- helpers ----------
function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function bucketize(tasks) {
  const today = startOfDay(new Date());
  const buckets = { overdue: [], today: [], planned: [] };
  for (const t of tasks) {
    if (t.completed) continue;
    if (!t.due_date) { buckets.planned.push(t); continue; }
    const parsed = parseDate(t.due_date);
    if (!parsed) { buckets.planned.push(t); continue; }
    const d = startOfDay(parsed);
    if (d < today) buckets.overdue.push(t);
    else if (d.getTime() === today.getTime()) buckets.today.push(t);
    else buckets.planned.push(t);
  }
  return buckets;
}

function dueDateColor(due_date, completed) {
  if (!due_date) return "text-[var(--ink-500)]";
  if (completed) return "text-[var(--ink-400)]";
  const today = startOfDay(new Date());
  const parsed = parseDate(due_date);
  if (!parsed) return "text-[var(--ink-500)]";
  const d = startOfDay(parsed);
  if (d < today) return "text-[var(--danger,#D6646E)]";
  if (d.getTime() === today.getTime()) return "text-[#D97706]";
  return "text-[var(--ink-500)]";
}

function formatDue(due_date) {
  if (!due_date) return "";
  const today = startOfDay(new Date());
  const parsed = parseDate(due_date);
  if (!parsed) return "—";
  const d = startOfDay(parsed);
  const diff = Math.round((d - today) / (1000 * 60 * 60 * 24));
  if (diff === 0) return "Vandaag";
  if (diff === 1) return "Morgen";
  if (diff === -1) return "Gisteren";
  if (diff < 0) return `${Math.abs(diff)}d te laat`;
  if (diff < 7) return `+${diff}d`;
  return parsed.toLocaleDateString("nl-BE", { day: "2-digit", month: "short" });
}
